import React, { useState, useEffect } from 'react';
import { 
  Globe, Users, Calendar, MessageCircle, BookOpen, Video, 
  Star, MapPin, Clock, Zap, Heart, Share2, Filter,
  Award, TrendingUp, Eye, ThumbsUp, MessageSquare,
  Play, Mic, Camera, Settings, Bell, Search,Code,
  Flag, User, Coffee, Lightbulb, Target, ArrowRight,
  ChevronDown, Plus, MoreHorizontal, Bookmark,
  UserPlus, Radio, Headphones, Download, Upload
} from 'lucide-react';

const EventLearning = ({ darkMode }) => {
  const [activeTab, setActiveTab] = useState('feed');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Sample data for global activities
  const globalActivities = [
    {
      id: 1,
      type: 'workshop',
      title: 'Advanced React Patterns Workshop',
      instructor: 'Sarah Chen',
      country: 'Singapore',
      flag: '🇸🇬',
      participants: 247,
      rating: 4.9,
      startTime: '2025-06-20T14:00:00Z',
      duration: '3 hours',
      topics: ['React', 'JavaScript', 'Frontend'],
      price: 'Free',
      description: 'Deep dive into advanced React patterns including render props, higher-order components, and custom hooks.',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b9234496?w=100&h=100&fit=crop&crop=face'
    },
    {
      id: 2,
      type: 'webinar',
      title: 'AI Ethics in Modern Development',
      instructor: 'Dr. James Mitchell',
      country: 'United Kingdom',
      flag: '🇬🇧',
      participants: 892,
      rating: 4.8,
      startTime: '2025-06-18T16:00:00Z',
      duration: '90 minutes',
      topics: ['AI', 'Ethics', 'Technology'],
      price: '$15',
      description: 'Exploring ethical considerations when implementing AI solutions in real-world applications.',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
    },
    {
      id: 3,
      type: 'study-group',
      title: 'Machine Learning Study Circle',
      instructor: 'Maria Rodriguez',
      country: 'Spain',
      flag: '🇪🇸',
      participants: 34,
      rating: 4.7,
      startTime: '2025-06-19T18:30:00Z',
      duration: '2 hours',
      topics: ['Machine Learning', 'Python', 'Data Science'],
      price: 'Free',
      description: 'Weekly study group discussing ML algorithms and practical implementations.',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face'
    },
    {
      id: 4,
      type: 'masterclass',
      title: 'UI/UX Design Masterclass',
      instructor: 'Alex Thompson',
      country: 'Canada',
      flag: '🇨🇦',
      participants: 156,
      rating: 4.9,
      startTime: '2025-06-21T20:00:00Z',
      duration: '4 hours',
      topics: ['Design', 'UX', 'Figma'],
      price: '$45',
      description: 'Comprehensive masterclass covering modern UI/UX principles and hands-on Figma practice.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face'
    }
  ];

  const communityFeed = [
    {
      id: 1,
      user: 'Elena Kowalski',
      avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=60&h=60&fit=crop&crop=face',
      country: 'Poland',
      flag: '🇵🇱',
      timestamp: '2 hours ago',
      type: 'achievement',
      content: 'Just completed the Advanced JavaScript course! The async/await patterns were challenging but so rewarding to master.',
      course: 'Advanced JavaScript Mastery',
      likes: 42,
      comments: 8,
      shares: 3,
      tags: ['JavaScript', 'Achievement', 'AsyncProgramming']
    },
    {
      id: 2,
      user: 'Ravi Patel',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&h=60&fit=crop&crop=face',
      country: 'India',
      flag: '🇮🇳',
      timestamp: '4 hours ago',
      type: 'question',
      content: 'Can anyone recommend good resources for learning GraphQL? I\'m comfortable with REST APIs but want to explore GraphQL for my next project.',
      likes: 28,
      comments: 15,
      shares: 5,
      tags: ['GraphQL', 'Question', 'WebDevelopment']
    },
    {
      id: 3,
      user: 'Sophie Martin',
      avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=60&h=60&fit=crop&crop=face',
      country: 'France',
      flag: '🇫🇷',
      timestamp: '6 hours ago',
      type: 'resource',
      content: 'Found this amazing free dataset for machine learning practice! Perfect for beginners working on classification problems.',
      resourceTitle: 'ML Classification Dataset Collection',
      resourceType: 'Dataset',
      likes: 67,
      comments: 12,
      shares: 23,
      tags: ['MachineLearning', 'Dataset', 'Resources']
    },
    {
      id: 4,
      user: 'Carlos Silva',
      avatar: 'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?w=60&h=60&fit=crop&crop=face',
      country: 'Brazil',
      flag: '🇧🇷',
      timestamp: '8 hours ago',
      type: 'collaboration',
      content: 'Looking for 2-3 developers to join a study group for AWS certification prep. We meet twice a week online!',
      likes: 35,
      comments: 22,
      shares: 8,
      tags: ['AWS', 'StudyGroup', 'Certification']
    }
  ];

  const topicFeedbacks = [
    {
      topic: 'React Hooks',
      rating: 4.6,
      reviews: 1247,
      difficulty: 'Intermediate',
      trend: 'up',
      recentFeedback: [
        { user: 'John D.', rating: 5, comment: 'UseEffect hook explained perfectly!' },
        { user: 'Lisa M.', rating: 4, comment: 'Great examples, could use more practice exercises.' },
        { user: 'Ahmed K.', rating: 5, comment: 'Finally understand custom hooks!' }
      ]
    },
    {
      topic: 'Python Data Analysis',
      rating: 4.8,
      reviews: 892,
      difficulty: 'Beginner',
      trend: 'up',
      recentFeedback: [
        { user: 'Sarah P.', rating: 5, comment: 'Pandas made so much easier to understand!' },
        { user: 'Mike R.', rating: 4, comment: 'Good pace, practical examples.' }
      ]
    },
    {
      topic: 'Docker Fundamentals',
      rating: 4.4,
      reviews: 567,
      difficulty: 'Intermediate',
      trend: 'stable',
      recentFeedback: [
        { user: 'Anna L.', rating: 4, comment: 'Container concepts well explained.' },
        { user: 'Dev S.', rating: 5, comment: 'Hands-on approach is excellent!' }
      ]
    }
  ];

  const suggestions = [
    {
      type: 'trending',
      title: 'Next.js 14 Features Deep Dive',
      reason: 'Trending in your region',
      participants: 2847,
      rating: 4.9,
      image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=300&h=200&fit=crop'
    },
    {
      type: 'personalized',
      title: 'Advanced TypeScript Patterns',
      reason: 'Based on your JavaScript progress',
      participants: 1456,
      rating: 4.7,
      image: 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=300&h=200&fit=crop'
    },
    {
      type: 'peer',
      title: 'System Design Interview Prep',
      reason: '89% of your connections are taking this',
      participants: 3421,
      rating: 4.8,
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=300&h=200&fit=crop'
    }
  ];

  const liveEvents = [
    {
      title: 'Live Q&A: Career in Tech',
      host: 'Google Engineers',
      viewers: 1247,
      country: 'USA',
      flag: '🇺🇸',
      isLive: true
    },
    {
      title: 'Coding Interview Practice',
      host: 'Meta Recruiters',
      viewers: 892,
      country: 'Ireland',
      flag: '🇮🇪',
      isLive: true
    }
  ];

  const getActivityIcon = (type) => {
    switch(type) {
      case 'workshop': return <Coffee className="w-5 h-5" />;
      case 'webinar': return <Video className="w-5 h-5" />;
      case 'study-group': return <Users className="w-5 h-5" />;
      case 'masterclass': return <Award className="w-5 h-5" />;
      default: return <BookOpen className="w-5 h-5" />;
    }
  };

  const getPostIcon = (type) => {
    switch(type) {
      case 'achievement': return <Award className="w-5 h-5 text-yellow-500" />;
      case 'question': return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'resource': return <Bookmark className="w-5 h-5 text-green-500" />;
      case 'collaboration': return <UserPlus className="w-5 h-5 text-purple-500" />;
      default: return <MessageSquare className="w-5 h-5" />;
    }
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b backdrop-blur-sm bg-opacity-90`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <h1 className="text-2xl font-bold">LearnHub Global</h1>
              
              <nav className="hidden md:flex items-center space-x-1">
                {['feed', 'events', 'community', 'topics'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-xl capitalize transition-all duration-200 ${
                      activeTab === tab 
                        ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                        : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100')
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search courses, events, people..."
                  className={`pl-10 pr-4 py-2 rounded-xl border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-100 border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
              
              <button className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'} transition-colors`}>
                <Bell className="w-5 h-5" />
              </button>
              
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Live Events Banner */}
        <div className={`p-4 rounded-2xl ${darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'} mb-8`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="font-medium text-red-600">LIVE NOW</span>
              </div>
              <div className="flex items-center space-x-6">
                {liveEvents.map((event, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm">{event.flag}</span>
                    <span className="text-sm font-medium">{event.title}</span>
                    <span className="text-xs text-gray-500">{event.viewers} watching</span>
                  </div>
                ))}
              </div>
            </div>
            <button className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center space-x-2">
              <Play className="w-4 h-4" />
              <span>Join Live</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'feed' && (
              <div className="space-y-6">
                <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                  <div className="flex items-center space-x-4 mb-4">
                    <img
                      src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=50&h=50&fit=crop&crop=face"
                      alt="Your avatar"
                      className="w-12 h-12 rounded-full"
                    />
                    <input
                      type="text"
                      placeholder="Share your learning journey..."
                      className={`flex-1 p-3 rounded-xl border ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                  </div>
                  <div className="flex items-center space-x-4">
                    <button className="flex items-center space-x-2 text-blue-500 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors">
                      <Camera className="w-4 h-4" />
                      <span>Photo</span>
                    </button>
                    <button className="flex items-center space-x-2 text-green-500 hover:bg-green-50 px-3 py-2 rounded-lg transition-colors">
                      <BookOpen className="w-4 h-4" />
                      <span>Resource</span>
                    </button>
                    <button className="flex items-center space-x-2 text-purple-500 hover:bg-purple-50 px-3 py-2 rounded-lg transition-colors">
                      <Users className="w-4 h-4" />
                      <span>Study Group</span>
                    </button>
                  </div>
                </div>

                {communityFeed.map((post) => (
                  <div key={post.id} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                    <div className="flex items-start space-x-4">
                      <img
                        src={post.avatar}
                        alt={post.user}
                        className="w-12 h-12 rounded-full"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="font-medium">{post.user}</h3>
                          <span className="text-lg">{post.flag}</span>
                          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {post.timestamp}
                          </span>
                          {getPostIcon(post.type)}
                        </div>
                        
                        <p className="mb-3">{post.content}</p>
                        
                        {post.course && (
                          <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-3`}>
                            <div className="flex items-center space-x-2">
                              <Award className="w-4 h-4 text-yellow-500" />
                              <span className="font-medium">{post.course}</span>
                            </div>
                          </div>
                        )}
                        
                        {post.resourceTitle && (
                          <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-3`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">{post.resourceTitle}</div>
                                <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {post.resourceType}
                                </div>
                              </div>
                              <Download className="w-5 h-5 text-blue-500" />
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.map((tag, index) => (
                            <span key={index} className={`px-2 py-1 text-xs rounded-full ${
                              darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                            }`}>
                              #{tag}
                            </span>
                          ))}
                        </div>
                        
                        <div className="flex items-center space-x-6 text-sm">
                          <button className="flex items-center space-x-2 hover:text-red-500 transition-colors">
                            <Heart className="w-4 h-4" />
                            <span>{post.likes}</span>
                          </button>
                          <button className="flex items-center space-x-2 hover:text-blue-500 transition-colors">
                            <MessageCircle className="w-4 h-4" />
                            <span>{post.comments}</span>
                          </button>
                          <button className="flex items-center space-x-2 hover:text-green-500 transition-colors">
                            <Share2 className="w-4 h-4" />
                            <span>{post.shares}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'events' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-light">Global Learning Events</h2>
                  <div className="flex items-center space-x-4">
                    <select 
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      className={`px-4 py-2 rounded-xl border ${
                        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                      }`}
                    >
                      <option value="all">All Countries</option>
                      <option value="us">🇺🇸 United States</option>
                      <option value="uk">🇬🇧 United Kingdom</option>
                      <option value="ca">🇨🇦 Canada</option>
                      <option value="sg">🇸🇬 Singapore</option>
                    </select>
                    
                    <select 
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className={`px-4 py-2 rounded-xl border ${
                        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                      }`}
                    >
                      <option value="all">All Types</option>
                      <option value="workshop">Workshops</option>
                      <option value="webinar">Webinars</option>
                      <option value="study-group">Study Groups</option>
                      <option value="masterclass">Masterclasses</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {globalActivities.map((activity) => (
                    <div key={activity.id} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg hover:shadow-xl transition-all duration-300`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          {getActivityIcon(activity.type)}
                          <div>
                            <div className={`text-xs font-medium uppercase tracking-wider ${
                              activity.type === 'workshop' ? 'text-blue-500' :
                              activity.type === 'webinar' ? 'text-purple-500' :
                              activity.type === 'study-group' ? 'text-green-500' :
                              'text-yellow-500'
                            }`}>
                              {activity.type.replace('-', ' ')}
                            </div>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-lg">{activity.flag}</span>
                              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {activity.country}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          activity.price === 'Free' 
                            ? (darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700')
                            : (darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-700')
                        }`}>
                          {activity.price}
                        </div>
                      </div>

                      <h3 className="text-xl font-semibold mb-2">{activity.title}</h3>
                      <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {activity.description}
                      </p>

                      <div className="flex items-center space-x-4 mb-4">
                        <img
                          src={activity.avatar}
                          alt={activity.instructor}
                          className="w-8 h-8 rounded-full"
                        />
                        <span className="font-medium">{activity.instructor}</span>
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          <span className="text-sm">{activity.rating}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {activity.topics.map((topic, index) => (
                          <span key={index} className={`px-2 py-1 text-xs rounded-full ${
                            darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {topic}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{activity.duration}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Users className="w-4 h-4" />
                            <span>{activity.participants} joined</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <button className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
                          darkMode 
                            ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}>
                          Join Event
                        </button>
                        <button className={`p-3 rounded-xl ${
                          darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                        } transition-colors`}>
                          <Bookmark className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'topics' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-light mb-6">Topic Feedback & Reviews</h2>
                
                {topicFeedbacks.map((topic, index) => (
                  <div key={index} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold mb-2">{topic.topic}</h3>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <Star className="w-5 h-5 text-yellow-500 fill-current" />
                            <span className="font-medium">{topic.rating}</span>
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              ({topic.reviews} reviews)
                            </span>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            topic.difficulty === 'Beginner' ? 'bg-green-100 text-green-700' :
                            topic.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {topic.difficulty}
                          </span>
                          <div className={`flex items-center space-x-1 ${
                            topic.trend === 'up' ? 'text-green-500' : 'text-gray-500'
                          }`}>
                            <TrendingUp className="w-4 h-4" />
                            <span className="text-sm">Trending</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-medium">Recent Feedback:</h4>
                      {topic.recentFeedback.map((feedback, idx) => (
                                                <div key={idx} className={`p-3 rounded-lg ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{feedback.user}</span>
                            <div className="flex items-center space-x-1">
                              <Star className="w-4 h-4 text-yellow-500 fill-current" />
                              <span>{feedback.rating}</span>
                            </div>
                          </div>
                          <p className={`text-sm ${
                            darkMode ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {feedback.comment}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 flex items-center space-x-3">
                      <button className={`flex-1 py-2 px-4 rounded-xl font-medium ${
                        darkMode 
                          ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                      } transition-colors`}>
                        View All Reviews
                      </button>
                      <button className={`py-2 px-4 rounded-xl font-medium ${
                        darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      } transition-colors`}>
                        Add Your Review
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'community' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-light">Global Learning Community</h2>
                  <button className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors">
                    <Plus className="w-4 h-4" />
                    <span>Create Group</span>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {suggestions.map((suggestion, index) => (
                    <div key={index} className={`rounded-2xl overflow-hidden shadow-lg ${
                      darkMode ? 'bg-gray-800' : 'bg-white'
                    }`}>
                      <div className="relative h-40">
                        <img
                          src={suggestion.image}
                          alt={suggestion.title}
                          className="w-full h-full object-cover"
                        />
                        <div className={`absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t ${
                          darkMode ? 'from-gray-900/90 to-transparent' : 'from-white/90 to-transparent'
                        }`}>
                          <h3 className="font-semibold">{suggestion.title}</h3>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className={`text-xs mb-2 ${
                          suggestion.type === 'trending' ? 'text-blue-500' :
                          suggestion.type === 'personalized' ? 'text-purple-500' :
                          'text-green-500'
                        }`}>
                          {suggestion.reason}
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-1">
                            <Users className="w-4 h-4" />
                            <span>{suggestion.participants}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span>{suggestion.rating}</span>
                          </div>
                        </div>
                        <button className={`w-full mt-4 py-2 rounded-xl font-medium ${
                          darkMode 
                            ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        } transition-colors`}>
                          Explore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* User Progress */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className="font-medium mb-4">Your Learning Progress</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">JavaScript Mastery</span>
                    <span className="text-sm font-medium">65%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: '65%' }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">Python Fundamentals</span>
                    <span className="text-sm font-medium">42%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full" 
                      style={{ width: '42%' }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">Data Structures</span>
                    <span className="text-sm font-medium">28%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full" 
                      style={{ width: '28%' }}
                    ></div>
                  </div>
                </div>
              </div>
              <button className={`w-full mt-4 py-2 rounded-xl font-medium ${
                darkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              } transition-colors`}>
                View All Courses
              </button>
            </div>

            {/* Upcoming Deadlines */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className="font-medium mb-4 flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>Upcoming Deadlines</span>
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    darkMode ? 'bg-red-500/20' : 'bg-red-100'
                  }`}>
                    <Calendar className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h4 className="font-medium">React Assignment</h4>
                    <div className={`text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Due tomorrow • 3 tasks remaining
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    darkMode ? 'bg-yellow-500/20' : 'bg-yellow-100'
                  }`}>
                    <Calendar className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h4 className="font-medium">Python Project</h4>
                    <div className={`text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Due in 3 days • 80% completed
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    darkMode ? 'bg-blue-500/20' : 'bg-blue-100'
                  }`}>
                    <Calendar className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="font-medium">Web Dev Quiz</h4>
                    <div className={`text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Due in 5 days • 2 chapters to review
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recommended Connections */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className="font-medium mb-4">Recommended Connections</h3>
              <div className="space-y-4">
                {[
                  {
                    name: 'Emma Wilson',
                    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face',
                    specialty: 'UI/UX Design',
                    mutual: 4
                  },
                  {
                    name: 'David Kim',
                    avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=60&h=60&fit=crop&crop=face',
                    specialty: 'Data Science',
                    mutual: 7
                  },
                  {
                    name: 'Sophia Martinez',
                    avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=60&h=60&fit=crop&crop=face',
                    specialty: 'Frontend Dev',
                    mutual: 3
                  }
                ].map((person, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <img
                        src={person.avatar}
                        alt={person.name}
                        className="w-10 h-10 rounded-full"
                      />
                      <div>
                        <div className="font-medium">{person.name}</div>
                        <div className={`text-xs ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {person.specialty} • {person.mutual} mutual connections
                        </div>
                      </div>
                    </div>
                    <button className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors">
                      <UserPlus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button className={`w-full text-center text-sm ${
                  darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'
                } transition-colors`}>
                  View all recommendations →
                </button>
              </div>
            </div>

            {/* Learning Resources */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className="font-medium mb-4">Quick Learning Resources</h3>
              <div className="space-y-3">
                {[
                  {
                    title: 'JavaScript Cheat Sheet',
                    type: 'PDF',
                    icon: <BookOpen className="w-5 h-5 text-blue-500" />
                  },
                  {
                    title: 'Python Crash Course',
                    type: 'Video',
                    icon: <Video className="w-5 h-5 text-red-500" />
                  },
                  {
                    title: 'CSS Grid Tutorial',
                    type: 'Interactive',
                    icon: <Code className="w-5 h-5 text-purple-500" />
                  },
                  {
                    title: 'React Podcast',
                    type: 'Audio',
                    icon: <Headphones className="w-5 h-5 text-green-500" />
                  }
                ].map((resource, index) => (
                  <div key={index} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                    <div className={`p-2 rounded-lg ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      {resource.icon}
                    </div>
                    <div>
                      <div className="font-medium">{resource.title}</div>
                      <div className={`text-xs ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {resource.type}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventLearning;